import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiPrinter, FiCheck, FiX, FiTruck, FiClock, FiSettings, 
  FiCheckCircle, FiDollarSign, FiPackage, FiMapPin, FiMail, FiPhone, FiInfo, FiCalendar } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const OrderDetails = ({ order, onBack, onStatusUpdate }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showTrackingInput, setShowTrackingInput] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    // If order is already complete data from parent, use it
    if (order && order.order_id) {
      setOrderData({
        ...order,
        amount: order.order_summary?.finalTotal || order.order_summary?.total || 0
      });
      setLoading(false);
      return;
    }
    
    // Otherwise fetch from API using order_id
    const fetchOrderDetails = async () => {
      try {
        const orderId = order.id || order.order_id;
        const response = await fetch(`${API_BASE_URL}/orders/${orderId}/`);
        if (!response.ok) {
          throw new Error('Failed to fetch order details');
        }
        const data = await response.json();
        setOrderData({
          ...data,
          amount: data.order_summary?.finalTotal || data.order_summary?.total || 0
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [order, API_BASE_URL]);

  const statusOptions = [
    { value: 'processing', label: 'Processing', icon: <FiSettings className="mr-2" />, color: 'blue' },
    { value: 'shipped', label: 'Shipped', icon: <FiTruck className="mr-2" />, color: 'purple' },
    // { value: 'out for delivery', label: 'Out For Delivery', icon: <FiTruck className="mr-2" />, color: 'orange' },
    { value: 'delivered', label: 'Delivered', icon: <FiCheckCircle className="mr-2" />, color: 'green' },
  ];

  // Function to send shipping notification email
  const sendShippingNotificationEmail = async (orderData, trackingNumber) => {
    try {
      const orderUrl = `${window.location.origin}/order/${orderData.order_id}`;
      
      // Format the order date
      const orderDate = new Date(orderData.created_at).toLocaleDateString();
      
      // Prepare required fields
      const emailData = {
        customer_email: orderData.billing_info?.email,
        customer_name: orderData.billing_info?.fullName,
        order_number: orderData.order_id,
        tracking_number: trackingNumber,
        order_url: orderUrl,
        order_date: orderDate
      };

      // Add shipping address as a combined string
      if (orderData.shipping_info) {
        const shippingAddressParts = [
          orderData.shipping_info.address,
          orderData.shipping_info.city,
          orderData.shipping_info.state,
          orderData.shipping_info.zip,
          orderData.shipping_info.country || 'India'
        ].filter(Boolean);
        
        emailData.shipping_address = shippingAddressParts.join(', ');
      }

      // Add optional fields if available
      if (orderData.cart_items && orderData.cart_items.length > 0) {
        emailData.products = orderData.cart_items;
      }
      
      if (orderData.order_summary) {
        emailData.subtotal = orderData.order_summary.subtotal;
        emailData.shipping_cost = orderData.order_summary.shippingCost;
        emailData.discount_amount = orderData.order_summary.discount;
      }

      // Add shipping method and estimated delivery
      emailData.shipping_method = "Standard Shipping";
      
      // Calculate estimated delivery date (7 days from order creation)
      const estimatedDelivery = new Date(orderData.created_at);
      estimatedDelivery.setDate(estimatedDelivery.getDate() + 7);
      emailData.estimated_delivery = estimatedDelivery.toISOString().split('T')[0];

      const response = await fetch(`${API_BASE_URL}/send-order-dispatch-email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        console.log('Shipping notification email sent successfully');
        toast.success('Shipping notification email sent to customer!', {
          position: "top-center",
          theme: "colored",
          autoClose: 3000,
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to send shipping notification email:', errorData);
        toast.warning(`Order updated but email notification failed: ${errorData.error || 'Unknown error'}`, {
          position: "top-center",
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error sending shipping notification email:', error);
      toast.warning(`Order updated but email notification failed: ${error.message}`, {
        position: "top-center",
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  // Function to send delivery notification email
  const sendDeliveryNotificationEmail = async (orderData) => {
    try {
      const orderUrl = `${window.location.origin}/order/${orderData.order_id}`;
      
      // Format products data as expected by the API
      const products = orderData.cart_items?.map(item => ({
        name: item.name,
        size: item.size || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        image: item.image
      })) || [];
      
      // Prepare required fields
      const emailData = {
        customer_email: orderData.billing_info?.email,
        customer_name: orderData.billing_info?.fullName,
        order_number: orderData.order_id,
        order_url: orderUrl,
        order_date: new Date(orderData.created_at).toISOString().split('T')[0], // Format date as YYYY-MM-DD
        products: products,
        shipping_address: orderData.shipping_info ? [
          orderData.shipping_info.address,
          orderData.shipping_info.city,
          orderData.shipping_info.state,
          orderData.shipping_info.zip,
          orderData.shipping_info.country || 'India'
        ].filter(Boolean).join(', ') : 'N/A',
        subtotal: orderData.order_summary?.subtotal || 0,
        shipping_cost: orderData.order_summary?.shippingCost || 0,
        tax_amount: 0, // Add tax if available in your data
        discount_amount: orderData.order_summary?.discount || 0,
        shipping_method: "Standard Shipping",
        total_amount: orderData.order_summary?.finalTotal || orderData.order_summary?.total || 0,
        delivery_date: new Date().toISOString().split('T')[0] // Today's date as delivery date
      };

      const response = await fetch(`${API_BASE_URL}/send-delivery-email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        console.log('Delivery notification email sent successfully');
        toast.success('Delivery confirmation email sent to customer!', {
          position: "top-center",
          theme: "colored",
          autoClose: 3000,
        });
      } else {
        const errorData = await response.json();
        console.error('Failed to send delivery notification email:', errorData);
        toast.warning(`Order updated but email notification failed: ${errorData.error || 'Unknown error'}`, {
          position: "top-center",
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error sending delivery notification email:', error);
      toast.warning(`Order updated but email notification failed: ${error.message}`, {
        position: "top-center",
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  const handleStatusUpdateWithTracking = async (newStatus) => {
    if (newStatus === 'shipped' && !trackingNumber) {
      setShowTrackingInput(true);
      return;
    }

    try {
      const payload = {
        order_id: orderData.order_id,
        status: newStatus,
        ...(newStatus === 'shipped' && { tracking_number: trackingNumber }),
      };

      const response = await fetch(`${API_BASE_URL}/update-order/${orderData.order_id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to update order status');
      }

      // Update local orderData with the new status and tracking number
      const updatedOrderData = {
        ...orderData,
        status: newStatus,
        ...(newStatus === 'shipped' && { tracking_number: trackingNumber }),
      };
      
      setOrderData(updatedOrderData);

      // Show success toast for order update
      toast.success(`Order status updated to ${newStatus}!`, {
        position: "top-center",
        theme: "colored",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Send appropriate email notification based on status
      if (newStatus === 'shipped' && trackingNumber) {
        await sendShippingNotificationEmail(updatedOrderData, trackingNumber);
      } else if (newStatus === 'delivered') {
        await sendDeliveryNotificationEmail(updatedOrderData);
      }

      // Reset tracking input
      setShowTrackingInput(false);
      setTrackingNumber('');

      // Call parent onStatusUpdate if provided
      if (onStatusUpdate) {
        onStatusUpdate(orderData.order_id, newStatus, newStatus === 'shipped' ? trackingNumber : '');
      }

    } catch (err) {
      // Show error toast
      toast.error(`Error: ${err.message}`, {
        position: "top-center",
        theme: "colored",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setError(err.message);
    }
  };

  // Function to update payment status
  const updatePaymentStatus = async (newPaymentStatus) => {
  try {
    const payload = {
      order_id: orderData.order_id,
      payment_status: newPaymentStatus
    };
    
    console.log("Sending payload:", payload);
    
    // Add more detailed headers
    const response = await fetch(`${API_BASE_URL}/update-payment-status/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    // Check if response is ok first
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // If it's JSON, parse it
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      } else {
        // If it's not JSON, get the text
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): Non-JSON response`);
      }
    }
    
    // Parse the JSON response
    const responseData = await response.json();
    
    // Update local orderData with the new payment status
    setOrderData({
      ...orderData,
      payment_status: newPaymentStatus
    });

    // Show success toast for payment status update
    toast.success(`Payment status updated to ${newPaymentStatus}!`, {
      position: "top-center",
      theme: "colored",
      autoClose: 3000,
    });

  } catch (err) {
    console.error("Payment status update error:", err);
    // Show error toast
    toast.error(`Error: ${err.message}`, {
      position: "top-center",
      theme: "colored",
      autoClose: 5000,
    });
  }
};

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'out for delivery': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-50 border-yellow-300 text-yellow-800';
      case 'processing': return 'bg-blue-50 border-blue-300 text-blue-800';
      case 'shipped': return 'bg-purple-50 border-purple-300 text-purple-800';
      case 'out for delivery': return 'bg-orange-50 border-orange-300 text-orange-800';
      case 'delivered': return 'bg-green-50 border-green-300 text-green-800';
      case 'cancelled': return 'bg-red-50 border-red-300 text-red-800';
      case 'paid': return 'bg-green-50 border-green-300 text-green-800';
      case 'failed': return 'bg-red-50 border-red-300 text-red-800';
      default: return 'bg-gray-50 border-gray-300 text-gray-800';
    }
  };

  const printInvoice = () => {
    console.log("Printing invoice for order", orderData.order_id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiX className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiInfo className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 font-medium">No order data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Format the created_at date
  const orderDate = new Date(orderData.created_at).toLocaleDateString();
  
  // Calculate estimated delivery date (7 days from order creation)
  const estimatedDeliveryDate = new Date(orderData.created_at);
  estimatedDeliveryDate.setDate(estimatedDeliveryDate.getDate() + 7);
  const formattedDeliveryDate = estimatedDeliveryDate.toLocaleDateString();

  // Calculate the display amount (using finalTotal if available, otherwise total)
  const displayAmount = orderData.order_summary?.finalTotal || orderData.order_summary?.total || 0;
  const hasDiscount = orderData.order_summary?.discount && orderData.order_summary?.finalTotal;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
      {/* Toast Container */}
      <ToastContainer />
      
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center mb-4 gap-4">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 bg-white rounded-lg hover:bg-gray-50 text-gray-700 transition-all shadow-sm border border-gray-200"
          >
            <FiArrowLeft className="mr-2" /> Back to Orders
          </button>
          <div className="flex items-center">
            <h3 className="text-xl font-bold text-gray-800">Order #{orderData.order_id.split('-')[0]}</h3>
            <span className={`ml-4 px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeColor(orderData.status)}`}>
              {orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
            </span>
          </div>
          <div className="flex items-center text-gray-500 ml-0 md:ml-auto gap-4">
            <span className="flex items-center text-sm">
              <FiCalendar className="mr-1" /> {orderDate}
            </span>
            {/* <button
              onClick={printInvoice}
              className="flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-indigo-700 border border-indigo-200 rounded-lg shadow-sm transition-all"
            >
              <FiPrinter className="mr-2" /> Print Invoice
            </button> */}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Order Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Customer Information */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-indigo-200 transition-all">
            <h4 className="font-medium text-lg mb-4 flex items-center text-gray-800">
              <FiMapPin className="mr-2 text-indigo-600" /> Customer Information
            </h4>
            <div className="space-y-3">
              <p className="text-gray-700 font-medium">{orderData.billing_info?.fullName || 'N/A'}</p>
              <p className="flex items-center text-gray-600">
                <FiMail className="mr-2 w-4 text-indigo-500" /> {orderData.billing_info?.email || 'N/A'}
              </p>
              <p className="flex items-center text-gray-600">
                <FiPhone className="mr-2 w-4 text-indigo-500" /> {orderData.billing_info?.phone || 'N/A'}
              </p>
              <div className="flex items-start text-gray-600">
                <FiMapPin className="mr-2 mt-1 w-4 flex-shrink-0 text-indigo-500" /> 
                <span>
                  {orderData.billing_info?.address || 'N/A'}, 
                  {orderData.billing_info?.city || 'N/A'}, 
                  {orderData.billing_info?.state || 'N/A'}, 
                  {orderData.billing_info?.zip || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-indigo-200 transition-all">
            <h4 className="font-medium text-lg mb-4 flex items-center text-gray-800">
              <FiDollarSign className="mr-2 text-indigo-600" /> Payment Information
            </h4>
            <div className="space-y-3">
              <p className="flex justify-between text-gray-600">
                <span>Method:</span>
                <span className="font-medium text-gray-800">
                  {orderData.billing_info?.paymentMethod === 'card' ? 'Online Payment' : 'COD'}
                </span>
              </p>
              <p className="flex justify-between items-center text-gray-600">
                <span>Status:</span> 
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                  getStatusBadgeColor(orderData.payment_status)
                }`}>
                  {orderData.payment_status?.charAt(0).toUpperCase() + orderData.payment_status?.slice(1) || 'Pending'}
                </span>
              </p>
              <p className="flex justify-between text-gray-600">
                <span>Amount:</span>
                <span className="font-medium text-gray-800">₹{displayAmount.toFixed(2)}</span>
              </p>
              <p className="flex justify-between text-gray-600">
                <span>Transaction ID:</span>
                <span className="font-medium text-gray-800">{(orderData._id || '').substring(0, 8)}</span>
              </p>
              <p className="flex justify-between text-gray-600">
                <span>Date:</span>
                <span className="font-medium text-gray-800">{orderDate}</span>
              </p>
            </div>
          </div>

          {/* Shipping Information */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:border-indigo-200 transition-all">
            <h4 className="font-medium text-lg mb-4 flex items-center text-gray-800">
              <FiTruck className="mr-2 text-indigo-600" /> Shipping Information
            </h4>
            <div className="space-y-3">
              <p className="flex justify-between text-gray-600">
                <span>Method:</span>
                <span className="font-medium text-gray-800">Standard Shipping</span>
              </p>
              <p className="flex justify-between text-gray-600">
                <span>Cost:</span>
                <span className="font-medium text-gray-800">
                  ₹{(orderData.order_summary?.shippingCost || 0).toFixed(2)}
                </span>
              </p>
              <p className="flex justify-between items-center text-gray-600">
                <span>Status:</span> 
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeColor(orderData.status)}`}>
                  {orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
                </span>
              </p>
              {orderData.tracking_number && (
                <p className="flex justify-between text-gray-600">
                  <span>Tracking #:</span>
                  <span className="font-medium text-gray-800">{orderData.tracking_number}</span>
                </p>
              )}
              <p className="flex justify-between text-gray-600">
                <span>Est. Delivery:</span>
                <span className="font-medium text-gray-800">{formattedDeliveryDate}</span>
              </p>
              <p className="text-gray-600 text-sm mt-2 truncate">
                <span className="block font-medium text-gray-700 mb-1">Shipping Address:</span>
                <span>
                  {orderData.shipping_info?.address || 'N/A'}, 
                  {orderData.shipping_info?.city || 'N/A'}, 
                  {orderData.shipping_info?.state || 'N/A'}, 
                  {orderData.shipping_info?.zip || 'N/A'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h4 className="font-medium text-lg flex items-center text-gray-800">
              <FiPackage className="mr-2 text-indigo-600" /> Order Items
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orderData.cart_items?.map((item) => (
                  <tr key={item.cart_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img className="h-12 w-12 rounded-lg object-cover shadow-sm border border-gray-200" src={item.image} alt={item.name} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">ID: {item.product_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.size}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{(item.unit_price || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">₹{(item.total_price || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td colSpan="4" className="px-6 py-3 text-right text-sm font-medium text-gray-500">Subtotal</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700">
                    ₹{(orderData.order_summary?.subtotal || 0).toFixed(2)}
                  </td>
                </tr>
                {hasDiscount && (
                  <tr className="bg-gray-50">
                    <td colSpan="4" className="px-6 py-3 text-right text-sm font-medium text-gray-500">Discount</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                      -₹{(orderData.order_summary?.discount || 0).toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr className="bg-gray-50">
                  <td colSpan="4" className="px-6 py-3 text-right text-sm font-medium text-gray-500">Shipping</td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700">
                    ₹{(orderData.order_summary?.shippingCost || 0).toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-indigo-50">
                  <td colSpan="4" className="px-6 py-4 text-right text-sm font-bold text-gray-700">Total</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-700">
                    ₹{displayAmount.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Tracking Number Input */}
        {showTrackingInput && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiTruck className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Tracking Number Required</h3>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  className="block w-full px-3 py-2 border border-yellow-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm"
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => handleStatusUpdateWithTracking('shipped')}
                    disabled={!trackingNumber}
                    className={`px-4 py-2 rounded-lg text-sm font-medium shadow-sm ${
                      !trackingNumber 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-purple-600 text-white hover:bg-purple-700 transition-colors'
                    }`}
                  >
                    <FiCheck className="inline mr-2" />
                    Confirm Shipment
                  </button>
                  <button
                    onClick={() => setShowTrackingInput(false)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center">
            <FiSettings className="mr-2 text-indigo-600" /> Update Order Status
          </h4>
          <div className="flex flex-wrap gap-3">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleStatusUpdateWithTracking(option.value)}
                disabled={orderData.status === option.value}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all ${
                  orderData.status === option.value 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : option.color === 'green'
                      ? 'bg-white border border-green-300 text-green-700 hover:bg-green-50'
                      : option.color === 'purple'
                        ? 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
                        : option.color === 'orange'
                          ? 'bg-white border border-orange-300 text-orange-700 hover:bg-orange-50'
                          : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-50'
                }`}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Email Notification Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <FiMail className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Email Notifications</p>
                <p className="text-xs text-blue-600">
                  • <strong>Shipped:</strong> Customer will receive shipping confirmation with tracking number<br/>
                  • <strong>Delivered:</strong> Customer will receive delivery confirmation email
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Payment Status Update Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mt-6">
          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center">
            <FiDollarSign className="mr-2 text-indigo-600" /> Update Payment Status
          </h4>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => updatePaymentStatus('pending')}
              disabled={orderData.payment_status === 'pending'}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all ${
                orderData.payment_status === 'pending' 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white border border-yellow-300 text-yellow-700 hover:bg-yellow-50'
              }`}
            >
              <FiClock className="mr-2" />
              Pending
            </button>
            <button
              onClick={() => updatePaymentStatus('paid')}
              disabled={orderData.payment_status === 'paid'}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all ${
                orderData.payment_status === 'paid' 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white border border-green-300 text-green-700 hover:bg-green-50'
              }`}
            >
              <FiCheckCircle className="mr-2" />
              Paid
            </button>
            <button
              onClick={() => updatePaymentStatus('failed')}
              disabled={orderData.payment_status === 'failed'}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-all ${
                orderData.payment_status === 'failed' 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white border border-red-300 text-red-700 hover:bg-red-50'
              }`}
            >
              <FiX className="mr-2" />
              Failed
            </button>
          </div>
          
          {/* Payment Update Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <FiInfo className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Payment Status</p>
                <p className="text-xs text-blue-600">
                  Update the payment status to reflect current payment information. 
                  This does not affect order status.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;